import * as XLSX from "xlsx";

/** One keyword x geo from a keywordtool export: m[0] is M1 (the newest month). */
export interface KeywordVolumeRow {
  keyword: string;
  geo: string;
  m: (number | null)[];
}

export interface ParsedKeywordFile {
  rows: KeywordVolumeRow[];
  /** When the export was made: the workbook's own date if it has one. */
  exportedAt: Date;
}

const MONTHS = Array.from({ length: 12 }, (_, i) => `m${i + 1}`);
const MIN_MONTH_COLUMNS = 6;

/** "—", "", null mean no data - not zero searches, which is a real value. */
export const cleanVolume = (value: unknown): number | null => {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const s = String(value ?? "").replace(/[\s,]/g, "");
  if (!/^-?\d+(\.\d+)?$/.test(s)) return null;
  return Number(s);
};

/** Rows of the "Monthly Data" layout: Keyword, Geo, (Volume), M1..M12, ... */
export const parseMonthlyRows = (table: unknown[][]): KeywordVolumeRow[] => {
  const headerIndex = table.findIndex((r) => {
    const cells = r.map((c) => String(c ?? "").trim().toLowerCase());
    return cells.includes("keyword") && cells.includes("geo");
  });
  if (headerIndex < 0) {
    throw new Error(
      "Wrong file: expected the Monthly Data sheet with Keyword, Geo and M1–M12 columns.",
    );
  }
  const header = table[headerIndex].map((c) => String(c ?? "").trim().toLowerCase());
  const kwCol = header.indexOf("keyword");
  const geoCol = header.indexOf("geo");
  // A missing M column is a month with no data. keywordtool does drop one now
  // and then: the 14.08.2026 export has M2..M12 and no M1.
  const mCols = MONTHS.map((m) => header.indexOf(m));
  if (kwCol < 0 || geoCol < 0 || mCols.filter((c) => c >= 0).length < MIN_MONTH_COLUMNS) {
    throw new Error(`Wrong file: Keyword, Geo and at least ${MIN_MONTH_COLUMNS} of M1–M12 are required.`);
  }

  const rows: KeywordVolumeRow[] = [];
  for (const r of table.slice(headerIndex + 1)) {
    const keyword = String(r[kwCol] ?? "").trim();
    const geo = String(r[geoCol] ?? "").trim().toUpperCase();
    if (!keyword || !geo) continue;
    rows.push({ keyword, geo, m: mCols.map((c) => (c < 0 ? null : cleanVolume(r[c]))) });
  }
  return rows;
};

/**
 * Which calendar month M1 is, as a fallback guess from the export date - used
 * only when a file shares no rows with stored data or the other files of its
 * batch (keyword-volume-m1.ts decides from the numbers first).
 * Measured over the 18 exports of 2026: keywordtool moves to a new month
 * between the 6th and the 14th of the next one - 05.08 still has June as M1,
 * 14.08 already July. So before the 10th, M1 is two months back.
 * ponytail: fixed day 10 inside a measured 6..14 window; the upload shows the
 * month and how it was found, and a date guess is marked for checking.
 */
export const defaultM1Month = (exportedAt: Date): string => {
  const back = exportedAt.getDate() >= 10 ? 1 : 2;
  const d = new Date(Date.UTC(exportedAt.getFullYear(), exportedAt.getMonth() - back, 1));
  return d.toISOString().slice(0, 10);
};

/** xls, xlsx and csv through one path. Prefers a sheet named "Monthly Data". */
export const readKeywordFile = async (file: File): Promise<ParsedKeywordFile> => {
  const wb = XLSX.read(await file.arrayBuffer(), { type: "array", cellDates: true });
  const name =
    wb.SheetNames.find((n) => n.trim().toLowerCase() === "monthly data") ?? wb.SheetNames[0];
  const table = XLSX.utils.sheet_to_json<unknown[]>(wb.Sheets[name], {
    header: 1,
    raw: true,
    defval: null,
  });
  const created = wb.Props?.CreatedDate;
  const exportedAt =
    created instanceof Date && !Number.isNaN(created.getTime())
      ? created
      : new Date(file.lastModified);
  return { rows: parseMonthlyRows(table), exportedAt };
};

/** "2026-08-01" -> "серп. 2026" (uk-UA default; Clicks passes "en-US" -> "Aug 2026"). */
export const formatMonth = (isoMonth: string, locale = "uk-UA"): string =>
  new Date(`${isoMonth}T00:00:00Z`).toLocaleDateString(locale, {
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });

/** Calendar month of M(n), n = 1..12, given M1's month. */
export const monthOf = (m1Month: string, n: number): string => {
  const d = new Date(`${m1Month}T00:00:00Z`);
  d.setUTCMonth(d.getUTCMonth() - (n - 1));
  return d.toISOString().slice(0, 10);
};

/** M1..M12 as an array from a month-keyed series, against the given M1 month. */
export const seriesToMonths = (series: { byMonth: Record<string, number> } | undefined, anchor: string) =>
  Array.from({ length: 12 }, (_, i) => series?.byMonth[monthOf(anchor, i + 1)] ?? null);

/** (M1+M2+M3)/(M4+M5+M6) - 1 in %, as keywordtool's "Trend (3vs3)". */
export const trend3vs3 = (months: (number | null)[]): number | null => {
  const sum = (a: (number | null)[]) => a.reduce<number>((s, v) => s + (v ?? 0), 0);
  const prev = sum(months.slice(3, 6));
  return prev > 0 ? Math.round((sum(months.slice(0, 3)) / prev - 1) * 1000) / 10 : null;
};

export interface VolumeMetrics {
  [month: `kv_m${number}`]: number | null;
  kv_last: number | null;
  kv_last_month: string | null;
  kv_avg: number | null;
  kv_trend_percent: number | null;
}

/**
 * Table metrics of one row's series, anchored on the row's OWN latest month.
 * Each keywordtool file is 12 months ending at its own M1, and files hold
 * different keyword sets: the newest month (Aug 2026) exists for only the 332
 * keywords of the two newest files. Falling coverage toward recent months is
 * that, not missing data - do not "fix" it with a global month or zeros.
 * Full model: docs/keyword-volume.md.
 */
export const volumeMetrics = (
  series: { byMonth: Record<string, number> } | undefined,
  /** Newest uploaded month: kv_m1..kv_m12 count back from it, same for every row. */
  anchor?: string,
): VolumeMetrics => {
  const byMonth = anchor
    ? Object.fromEntries(seriesToMonths(series, anchor).map((v, i) => [`kv_m${i + 1}`, v]))
    : {};
  const months = Object.keys(series?.byMonth ?? {}).sort();
  const last = months.at(-1);
  if (!series || !last) return { ...byMonth, kv_last: null, kv_last_month: null, kv_avg: null, kv_trend_percent: null };
  const own = seriesToMonths(series, last);
  const known = own.filter((v): v is number => v != null);
  return {
    ...byMonth,
    kv_last: series.byMonth[last],
    kv_last_month: last,
    kv_avg: Math.round(known.reduce((s, v) => s + v, 0) / known.length),
    kv_trend_percent: trend3vs3(own),
  };
};
